import { type MutableRefObject, useLayoutEffect } from 'react'
import { clampScrollerToRange } from '../helpers/clamp-scroll'
import type { PendingCtrlWheelAnchor } from '../types/pending-ctrl-wheel-anchor'

/**
 * Scroll correction when the explicit zoom changes: keep the pointer fixed after a ctrl+wheel /
 * pinch zoom, otherwise keep the rulers' (0, 0) fixed in the viewport.
 */
export function useZoomScrollAnchor({
  scrollRef,
  zoom,
  scale,
  snappedSpreadOriginCanvasX,
  verticalRulerZeroAtCanvasY,
  pendingCtrlWheelAnchorRef,
  prevExplicitZoomAnchorRef,
  getCanvasOrigin,
  syncRulerScrollTransforms,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  zoom: number
  scale: number
  snappedSpreadOriginCanvasX: number
  verticalRulerZeroAtCanvasY: number
  pendingCtrlWheelAnchorRef: MutableRefObject<PendingCtrlWheelAnchor | null>
  prevExplicitZoomAnchorRef: MutableRefObject<number | null>
  getCanvasOrigin: () => { originX: number; originY: number }
  syncRulerScrollTransforms: () => void
}) {
  /**
   * When explicit zoom changes, keep the canvas point where the rulers show (0, 0) fixed in the viewport.
   * Same `horizontalZeroAtCanvasX` / `verticalZeroAtCanvasY` as ruler tick math (`compute-ruler-ticks`).
   * Runs after `scale` matches `zoom` so the DOM scale is current.
   */
  useLayoutEffect(() => {
    const scroller = scrollRef.current

    if (!scroller) return

    if (zoom === 0) {
      prevExplicitZoomAnchorRef.current = null
      pendingCtrlWheelAnchorRef.current = null
      return
    }

    if (scale !== zoom) return

    let pending = pendingCtrlWheelAnchorRef.current

    if (pending && +pending.to.toFixed(3) !== +zoom.toFixed(3)) {
      pendingCtrlWheelAnchorRef.current = null
      pending = null
    }

    if (pending && +pending.to.toFixed(3) === +zoom.toFixed(3)) {
      const { contentX, contentY, clientX, clientY } = pending

      pendingCtrlWheelAnchorRef.current = null

      // contentX/contentY were captured before this resize — do not re-derive them from
      // scroller.scrollLeft/scrollTop here, the browser may have already clamped those to
      // the new (possibly smaller) scroll range as a side effect of the pasteboard resizing.
      const scRect = scroller.getBoundingClientRect()
      const cx = clientX - scRect.left
      const cy = clientY - scRect.top
      const { originX, originY } = getCanvasOrigin()
      const nextScrollLeft = contentX * zoom + originX - cx
      const nextScrollTop = contentY * zoom + originY - cy

      clampScrollerToRange(scroller, nextScrollLeft, nextScrollTop)

      syncRulerScrollTransforms()

      prevExplicitZoomAnchorRef.current = zoom

      return
    }

    const prevZ = prevExplicitZoomAnchorRef.current

    prevExplicitZoomAnchorRef.current = zoom

    if (prevZ == null) return

    const delta = zoom - prevZ

    if (Math.abs(delta) < 1e-9) return

    const focalX = snappedSpreadOriginCanvasX
    const focalY = verticalRulerZeroAtCanvasY

    clampScrollerToRange(
      scroller,
      scroller.scrollLeft + focalX * delta,
      scroller.scrollTop + focalY * delta
    )

    syncRulerScrollTransforms()
  }, [
    zoom,
    scale,
    snappedSpreadOriginCanvasX,
    verticalRulerZeroAtCanvasY,
    syncRulerScrollTransforms,
    getCanvasOrigin,
    scrollRef,
    pendingCtrlWheelAnchorRef,
    prevExplicitZoomAnchorRef,
  ])
}
